'use client';

import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import StickyBottomBar from '@/components/StickyBottomBar';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const lang = 'it';

const locations = [
  {
    id: 'loc1',
    name: 'Alice Beach',
    address: 'Via Leonardo Da Vinci, Villaggi, NO',
    desc: 'Alice Beach\n\nUna delle spiagge più accoglienti del Lago Maggiore, perfetta per vivere la giornata in totale relax.\n\nDog friendly e facilmente accessibile, con parcheggio comodo e numerosi servizi, è una realtà dinamica dove convivono diverse attività sull\'acqua come SUP, waterbike e noleggi.\n\nUn ambiente informale ma curato, ideale per chi cerca una location completa, viva e aperta a tutti.',
    img: '/assets/images/spiaggia_alice_beach-1773435101786.jpg',
    alt: 'Spiaggia Alice Beach sul Lago Maggiore con acque calme e panorama sulle Isole Borromee',
    mapsUrl: 'https://maps.google.com/?q=Via+Leonardo+Da+Vinci+Villaggi+NO',
    bookingParam: 'loc1',
    websiteUrl: 'https://www.alicebeach.it',
    logo: '/assets/images/bozza_logo_Alice_Beach_sito-1778954207544.png',
  },
  {
    id: 'loc2',
    name: 'Spiaggia Miami',
    address: 'Via Novara 69, Orta San Giulio, NO',
    desc: 'Una realtà curata e riconoscibile sul Lago d\'Orta, dove ogni dettaglio è pensato per offrire un\'esperienza di qualità.\n\nTra ristorante, bar e servizi attrezzati, non è solo una spiaggia ma un punto di riferimento per chi vuole vivere il lago con stile e tranquillità.\n\nAffacciata su acque calme e cristalline, è il luogo ideale per rallentare, godersi il panorama e passare una giornata completa tra relax e comfort.',
    img: '/assets/images/foto_spiaggia_miami-1773431325078.jpg',
    alt: 'Spiaggia Miami a Orta San Giulio con baia protetta e acque cristalline',
    mapsUrl: 'https://maps.google.com/?q=QCFJ%2BJ4+Orta+San+Giulio,+Province+of+Novara',
    bookingParam: 'loc2',
    websiteUrl: 'https://spiaggiamiami.com',
    logo: '/assets/images/logopng-1778845727035.png',
  },
];

export default function LocationPageIT() {
  return (
    <>
      <Header lang={lang} />
      <main>
        {/* ── Hero ── */}
        <section
          style={{
            padding: 'clamp(60px, 10vw, 100px) 0 clamp(40px, 6vw, 60px)',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Background glow */}
          <div
            style={{
              position: 'absolute',
              top: '-80px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '600px',
              height: '400px',
              background: 'radial-gradient(ellipse, rgba(0,212,255,0.12) 0%, transparent 70%)',
              pointerEvents: 'none',
            }}
          />
          <div className="container-main" style={{ position: 'relative' }}>
            <div className="badge badge-cyan" style={{ marginBottom: '16px', display: 'inline-flex' }}>
              <Icon name="MapPinIcon" size={12} variant="solid" />
              Le nostre location
            </div>
            <h1
              style={{
                fontFamily: 'Fraunces, Georgia, serif',
                fontSize: 'clamp(2.2rem, 6vw, 4rem)',
                fontWeight: 700,
                letterSpacing: '-0.03em',
                lineHeight: 1.1,
                marginBottom: '20px',
              }}
            >
              Vola sull&apos;acqua,{' '}
              <span style={{ color: '#00D4FF' }}>scegli la tua spiaggia</span>
            </h1>
            <p
              style={{
                fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                color: 'rgba(240,247,255,0.6)',
                maxWidth: '560px',
                margin: '0 auto 32px',
                lineHeight: 1.7,
              }}
            >
              Due location attive sul Lago Maggiore e Lago d&apos;Orta. Scegli quella più vicina a te e prenota la tua sessione e-foil.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link href="/booking" className="btn-primary" style={{ padding: '12px 24px' }}>
                <Icon name="CalendarDaysIcon" size={16} variant="solid" />
                Prenota ora
              </Link>
              <a
                href="https://wa.me/393757934676"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp"
                style={{ padding: '12px 24px' }}
              >
                <Icon name="ChatBubbleLeftRightIcon" size={16} />
                Chiedi info
              </a>
            </div>
          </div>
        </section>

        {/* ── Location Cards ── */}
        <section style={{ padding: '0 0 var(--section-pad)' }}>
          <div className="container-main">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '48px' }}>
              {locations?.map((loc, idx) => (
                <div
                  key={loc?.id}
                  style={{
                    background: 'rgba(18,41,64,0.6)',
                    border: '1px solid rgba(240,247,255,0.08)',
                    borderRadius: '24px',
                    overflow: 'hidden',
                  }}
                >
                  {/* Image + Map row */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    }}
                  >
                    {/* Photo */}
                    <div style={{ position: 'relative', aspectRatio: '16/9', minHeight: '240px', overflow: 'hidden' }}>
                      <AppImage
                        src={loc?.img}
                        alt={loc?.alt}
                        fill
                        className="object-cover"
                        style={{ objectFit: 'cover', transition: 'transform 0.6s ease' }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          background: 'linear-gradient(to top, rgba(11,31,46,0.6) 0%, transparent 50%)',
                        }}
                      />
                      {/* Active badge */}
                      <div
                        className="badge badge-green"
                        style={{ position: 'absolute', top: '14px', left: '14px', fontSize: '11px' }}
                      >
                        <span
                          style={{
                            width: '7px',
                            height: '7px',
                            borderRadius: '50%',
                            background: '#25D366',
                            display: 'inline-block',
                          }}
                        />
                        Attiva
                      </div>
                      {/* Location number */}
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '14px',
                          right: '14px',
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          background: 'rgba(0,212,255,0.15)',
                          border: '1px solid rgba(0,212,255,0.4)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontFamily: 'Fraunces, Georgia, serif',
                          fontWeight: 700,
                          fontSize: '16px',
                          color: '#00D4FF',
                        }}
                      >
                        {idx + 1}
                      </div>
                    </div>

                    {/* Map placeholder */}
                    <div
                      style={{
                        position: 'relative',
                        minHeight: '240px',
                        background: 'rgba(11,31,46,0.8)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '12px',
                        borderLeft: '1px solid rgba(240,247,255,0.06)',
                      }}
                    >
                      {/* Map grid pattern */}
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          backgroundImage:
                            'linear-gradient(rgba(0,212,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.04) 1px, transparent 1px)',
                          backgroundSize: '32px 32px',
                        }}
                      />
                      <div
                        style={{
                          position: 'relative',
                          width: '56px',
                          height: '56px',
                          borderRadius: '50%',
                          background: 'rgba(0,212,255,0.1)',
                          border: '2px solid rgba(0,212,255,0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Icon name="MapPinIcon" size={26} variant="solid" style={{ color: '#00D4FF' }} />
                      </div>
                      <div style={{ position: 'relative', textAlign: 'center', padding: '0 24px' }}>
                        <p style={{ fontSize: '13px', color: 'rgba(240,247,255,0.5)', marginBottom: '4px' }}>
                          {loc?.address}
                        </p>
                        <a
                          href={loc?.mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '12px',
                            color: '#00D4FF',
                            fontWeight: 600,
                            textDecoration: 'none',
                            marginTop: '8px',
                          }}
                        >
                          <Icon name="ArrowTopRightOnSquareIcon" size={13} />
                          Apri in Google Maps
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Info row */}
                  <div style={{ padding: '28px 32px 32px' }}>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: '20px',
                      }}
                    >
                      <div style={{ flex: '1 1 280px' }}>
                        {/* Top row: meta + title + desc side by side on desktop */}
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.4fr)',
                            gap: '24px',
                            alignItems: 'start',
                          }}
                          className="location-info-grid"
                        >
                          {/* Left: address badge + name + subtitle + link */}
                          <div>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '6px',
                              }}
                            >
                              <Icon name="MapPinIcon" size={15} variant="solid" style={{ color: '#00D4FF' }} />
                              <span style={{ fontSize: '12px', color: '#00D4FF', fontWeight: 600, letterSpacing: '0.04em' }}>
                                {loc?.address}
                              </span>
                            </div>
                            <h2
                              style={{
                                fontFamily: 'Fraunces, Georgia, serif',
                                fontSize: 'clamp(1.4rem, 3vw, 2rem)',
                                fontWeight: 700,
                                letterSpacing: '-0.02em',
                                marginBottom: '6px',
                              }}
                            >
                              {loc?.name}
                            </h2>
                            <p
                              style={{
                                fontSize: '12px',
                                color: 'rgba(240,247,255,0.35)',
                                fontStyle: 'italic',
                                letterSpacing: '0.02em',
                                marginBottom: '14px',
                              }}
                            >
                              In collaborazione con {loc?.name}
                            </p>
                            {loc?.websiteUrl && (
                              <a
                                href={loc.websiteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '7px',
                                  fontSize: '13px',
                                  color: 'rgba(240,247,255,0.55)',
                                  fontWeight: 500,
                                  textDecoration: 'none',
                                  border: '1px solid rgba(240,247,255,0.12)',
                                  borderRadius: '8px',
                                  padding: '8px 16px',
                                  transition: 'color 0.2s, border-color 0.2s',
                                }}
                                onMouseEnter={e => {
                                  (e.currentTarget as HTMLAnchorElement).style.color = '#00D4FF';
                                  (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(0,212,255,0.3)';
                                }}
                                onMouseLeave={e => {
                                  (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(240,247,255,0.55)';
                                  (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(240,247,255,0.12)';
                                }}
                              >
                                <Icon name="ArrowTopRightOnSquareIcon" size={13} />
                                Scopri la location
                              </a>
                            )}
                            {(loc as typeof loc & { logo?: string })?.logo && (
                              <div style={{ marginTop: '16px' }}>
                                {loc.id === 'loc1' ? (
                                  <AppImage
                                    src={(loc as typeof loc & { logo?: string }).logo!}
                                    alt={`Logo ${loc.name}`}
                                    width={140}
                                    height={140}
                                    style={{
                                      objectFit: 'contain',
                                      width: '140px',
                                      height: 'auto',
                                      display: 'block',
                                      background: 'transparent',
                                      border: 'none',
                                      padding: 0,
                                    }}
                                  />
                                ) : (
                                  <AppImage
                                    src={(loc as typeof loc & { logo?: string }).logo!}
                                    alt={`Logo ${loc.name}`}
                                    width={200}
                                    height={100}
                                    style={{ objectFit: 'contain', borderRadius: '8px', maxWidth: '200px', height: 'auto' }}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                          {/* Right: description */}
                          <p
                            style={{
                              fontSize: '15px',
                              color: 'rgba(240,247,255,0.6)',
                              lineHeight: 1.7,
                              whiteSpace: 'pre-wrap',
                              marginTop: '4px',
                            }}
                          >
                            {loc?.desc}
                          </p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flexShrink: 0 }}>
                        <Link
                          href={`/booking?location=${loc?.bookingParam}`}
                          className="btn-primary"
                          style={{ padding: '12px 24px', fontSize: '14px' }}
                        >
                          <Icon name="CalendarDaysIcon" size={15} variant="solid" />
                          Prenota qui
                        </Link>
                        <a
                          href={loc?.mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-secondary"
                          style={{ padding: '10px 20px', fontSize: '13px' }}
                        >
                          <Icon name="MapPinIcon" size={14} />
                          Come arrivare
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* ── Coming Soon Card ── */}
              <div
                style={{
                  background: 'rgba(18,41,64,0.4)',
                  border: '1.5px dashed rgba(232,169,77,0.2)',
                  borderRadius: '24px',
                  padding: 'clamp(40px, 6vw, 64px) 32px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: '16px',
                }}
              >
                <div className="badge badge-gold" style={{ fontSize: '11px' }}>
                  Prossimamente
                </div>
                <div
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'rgba(232,169,77,0.08)',
                    border: '1px solid rgba(232,169,77,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon name="MapPinIcon" size={28} style={{ color: '#E8A94D' }} />
                </div>
                <h2
                  style={{
                    fontFamily: 'Fraunces, Georgia, serif',
                    fontSize: 'clamp(1.4rem, 3vw, 1.8rem)',
                    fontWeight: 700,
                    color: 'rgba(240,247,255,0.55)',
                    letterSpacing: '-0.02em',
                  }}
                >
                  Nuove location in arrivo
                </h2>
                <p
                  style={{
                    fontSize: '15px',
                    color: 'rgba(240,247,255,0.35)',
                    maxWidth: '400px',
                    lineHeight: 1.7,
                  }}
                >
                  Stiamo espandendo la nostra rete sul Lago Maggiore e oltre. Seguici per essere il primo a sapere quando apriremo nuovi punti di accesso.
                </p>
                <a
                  href="https://wa.me/393757934676"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                  style={{ padding: '10px 20px', fontSize: '13px', borderColor: 'rgba(232,169,77,0.25)', color: '#E8A94D' }}
                >
                  <Icon name="BellIcon" size={14} />
                  Avvisami quando apre
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── Collaboration Section ── */}
        <section
          style={{
            padding: 'clamp(60px, 8vw, 100px) 0',
            borderTop: '1px solid rgba(240,247,255,0.06)',
          }}
        >
          <div
            className="container-main"
            style={{
              maxWidth: '640px',
              textAlign: 'center',
            }}
          >
            <h2
              style={{
                fontFamily: 'Fraunces, Georgia, serif',
                fontSize: 'clamp(1.5rem, 3.5vw, 2.2rem)',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
                marginBottom: '20px',
                color: 'rgba(240,247,255,0.9)',
              }}
            >
              Collaboriamo con nuove location
            </h2>
            <p
              style={{
                fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                color: 'rgba(240,247,255,0.45)',
                lineHeight: 1.8,
                marginBottom: '36px',
              }}
            >
              Siamo sempre alla ricerca di nuove realtà sul lago con cui collaborare.
              <br /><br />
              Se gestisci una spiaggia, un lido o una struttura e vuoi offrire un&apos;esperienza e-foil ai tuoi clienti, possiamo costruire insieme una collaborazione semplice e sostenibile.
            </p>
            <a
              href="https://wa.me/393757934676"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: 600,
                color: 'rgba(240,247,255,0.75)',
                textDecoration: 'none',
                border: '1px solid rgba(240,247,255,0.18)',
                borderRadius: '10px',
                padding: '12px 28px',
                letterSpacing: '0.02em',
                transition: 'color 0.2s, border-color 0.2s',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLAnchorElement).style.color = '#00D4FF';
                (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(0,212,255,0.35)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(240,247,255,0.75)';
                (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(240,247,255,0.18)';
              }}
            >
              <Icon name="ChatBubbleLeftRightIcon" size={15} />
              Contattaci
            </a>
          </div>
        </section>
      </main>
      <Footer lang={lang} />
      <StickyBottomBar lang={lang} />
    </>
  );
}
